import { useEffect, useLayoutEffect, useRef, useState, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '../utils.ts';
import './form.css';

/* ---------------------------------------------------------------------------------------------
 * Checkbox — native input restyled, so forms and a11y just work
 * ------------------------------------------------------------------------------------------- */

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type' | 'onChange' | 'size'> {
  checked?: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md';
}

export function Checkbox({ checked, indeterminate, onCheckedChange, label, description, size = 'md', className, ...rest }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  const box = (
    <input
      ref={ref}
      type="checkbox"
      className="g-checkbox"
      data-size={size}
      checked={!!checked}
      onChange={(e) => onCheckedChange?.(e.target.checked)}
      {...rest}
    />
  );
  if (!label) return <span className={cx('g-checkbox-wrap', className)}>{box}</span>;
  return (
    <label className={cx('g-choice', className)}>
      {box}
      <span className="g-choice__text">
        <span className="g-choice__label">{label}</span>
        {description && <span className="g-choice__desc">{description}</span>}
      </span>
    </label>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Switch
 * ------------------------------------------------------------------------------------------- */

export interface SwitchProps extends Omit<ComponentProps<'button'>, 'onChange'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md';
}

export function Switch({ checked, onCheckedChange, label, description, size = 'md', className, disabled, ...rest }: SwitchProps) {
  const control = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className="g-switch"
      data-size={size}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      {...rest}
    >
      <span className="g-switch__thumb" />
    </button>
  );
  if (!label) return <span className={className}>{control}</span>;
  return (
    <label className={cx('g-choice g-choice--switch', className)} data-disabled={disabled || undefined}>
      <span className="g-choice__text">
        <span className="g-choice__label">{label}</span>
        {description && <span className="g-choice__desc">{description}</span>}
      </span>
      {control}
    </label>
  );
}

/* ---------------------------------------------------------------------------------------------
 * SegmentedControl — radio group with a sliding thumb
 * ------------------------------------------------------------------------------------------- */

export interface Segment<V extends string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  /** Accessible label when the segment is icon-only. */
  ariaLabel?: string;
}

export interface SegmentedControlProps<V extends string> {
  value: V;
  onChange: (value: V) => void;
  options: Segment<V>[];
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
  block?: boolean;
}

export function SegmentedControl<V extends string>({ value, onChange, options, size = 'md', className, block, ...aria }: SegmentedControlProps<V>) {
  const root = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const el = root.current?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (el) setThumb({ left: el.offsetLeft, width: el.offsetWidth });
  }, [value, options.length]);
  const onKey = (e: KeyboardEvent) => {
    const i = options.findIndex((o) => o.value === value);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(options[(i + 1) % options.length]!.value);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(options[(i - 1 + options.length) % options.length]!.value);
    }
  };
  return (
    <div ref={root} role="radiogroup" aria-label={aria['aria-label']} className={cx('g-segmented', block && 'g-segmented--block', className)} data-size={size} onKeyDown={onKey}>
      {thumb && <span className="g-segmented__thumb" style={{ transform: `translateX(${thumb.left}px)`, width: thumb.width }} />}
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          aria-label={o.ariaLabel}
          tabIndex={o.value === value ? 0 : -1}
          className="g-segmented__item"
          onClick={() => onChange(o.value)}
        >
          {o.icon}
          {o.label !== undefined && o.label !== null && o.label !== '' && <span>{o.label}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Tabs — underline style, for view switchers and settings sections
 * ------------------------------------------------------------------------------------------- */

export interface TabItem<V extends string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
}

export interface TabsProps<V extends string> {
  value: V;
  onChange: (value: V) => void;
  items: TabItem<V>[];
  className?: string;
  size?: 'sm' | 'md';
  /** Extra content at the end of the tab row. */
  after?: ReactNode;
}

export function Tabs<V extends string>({ value, onChange, items, className, size = 'md', after }: TabsProps<V>) {
  const root = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const el = root.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    setBar(el ? { left: el.offsetLeft, width: el.offsetWidth } : null);
  }, [value, items]);
  const onKey = (e: KeyboardEvent) => {
    const i = items.findIndex((t) => t.value === value);
    if (e.key === 'ArrowRight') onChange(items[(i + 1) % items.length]!.value);
    if (e.key === 'ArrowLeft') onChange(items[(i - 1 + items.length) % items.length]!.value);
  };
  return (
    <div className={cx('g-tabs', className)} data-size={size}>
      <div ref={root} role="tablist" className="g-tabs__list" onKeyDown={onKey}>
        {items.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={t.value === value}
            tabIndex={t.value === value ? 0 : -1}
            className="g-tabs__tab"
            onClick={() => onChange(t.value)}
          >
            {t.icon}
            <span>{t.label}</span>
            {t.count !== undefined && <span className="g-tabs__count">{t.count}</span>}
          </button>
        ))}
        {bar && <span className="g-tabs__bar" style={{ transform: `translateX(${bar.left}px)`, width: bar.width }} />}
      </div>
      {after && <div className="g-tabs__after">{after}</div>}
    </div>
  );
}
